import * as React from "react";
import classNames from 'classnames';
import Cookies from 'js-cookie';
import i18n from "../i18n";
import { useSelector } from 'react-redux';
import { Frame } from "./frame/frame-container";
import { ChatContainer } from "./layout/chat-container/chat-container";
import { store } from '../store/index';
import { IInitialState, initialState, DataContext, ScreenView, WebChatDTO, TicketDTO, TicketMessageDTO, WebChatSettingsDTO, WebchatPosition } from '../models/chat.models';
import { webChatService } from '../services/webchat.service';

export interface WebChatProps { webChatSettings: WebChatSettingsDTO; }

export const WebChat = (props: WebChatProps) => {

  // Use Redux state directly without mutating context
  const chatOpened = useSelector<IInitialState, boolean>(state => state.chatOpened);
  const screenView = useSelector<IInitialState, ScreenView>(state => state.screenView);
  const welcomeMessage = useSelector<IInitialState, boolean>(state => state.welcomeMessage);
  const subscribedChat = useSelector<IInitialState, boolean>(state => state.subscribedChat);
  const webChatDto = useSelector<IInitialState, WebChatDTO>(state => state.webChatDto);
  const message = useSelector<IInitialState, string>(state => state.message);
  const webchatHeight = useSelector<IInitialState, number>(state => state.webchatHeight);
  const messageHeight = useSelector<IInitialState, number>(state => state.messageHeight);
  const messages = useSelector<IInitialState, TicketMessageDTO[]>(state => state.messages);
  const isThinking = useSelector<IInitialState, boolean>(state => state.isThinking);
  
  // Create context object from Redux state
  const context: IInitialState = {
    chatOpened,
    screenView,
    welcomeMessage,
    subscribedChat,
    webChatDto,
    message,
    webchatHeight,
    messageHeight,
    messages,
    isThinking,
    webChatSettings: props.webChatSettings
  };
  
  const [getActiveChatDisable, setGetActiveChatDisable] = React.useState(false);

  const css = require('./web-chat.css').toString();
  const cssFrame = require('./frame/frame-container.css').toString();

  let personalizedCss = '';
  let customCssUrl = '';
  
  async function showHideChat(): Promise<void> {
    if (context.chatOpened) {
      store.dispatch({ type: 'SET_CHAT_OPENED', payload: false });
    } else {
      store.dispatch({ type: 'SET_CHAT_OPENED', payload: true });
      setGetActiveChatDisable(true);
      setTimeout(async function() {
        try {
          await getActiveChat();
          store.dispatch({ type: 'SET_CHAT_OPENED', payload: true });
        } catch(ex) {
        } finally {
          setGetActiveChatDisable(false);
        }
      }, 0);
    }
  }

  async function getActiveChat(): Promise<void>  {
    if (!context.webChatDto.Session) {
      let session = Cookies.get("wch_session");
      let webChat: WebChatDTO = await webChatService.getActiveChat(session);
      if (webChat) {
        if (webChat.Lang) {
          i18n.changeLanguage(webChat.Lang);
        } else {
          let lang = Cookies.get("lang");
          if (lang) {
            i18n.changeLanguage(lang);
          }
        }
        Cookies.set("wch_session", webChat.Session, { expires: 1 });
        store.dispatch({ type: 'SET_WEB_CHAT', payload: webChat });
        store.dispatch({ type: 'SHOW_WELCOME_MESSAGE', payload: !webChat.ForceOpen });
        store.dispatch({ type: 'SET_WEB_CHAT_MESSAGES', payload: webChat.Ticket && webChat.Ticket && webChat.Ticket.TicketResponseMessages ? webChat.Ticket.TicketResponseMessages : [] });
      }
    }
  }

  if (context.webChatDto && context.webChatDto.ForceOpen) {
    store.dispatch({ type: 'SET_CHAT_OPENED', payload: true });
  }

  React.useEffect(() => {
    webChatService.log("WebChat componentDidMount");
  }, []);

  if (context.webChatSettings && context.webChatSettings.MainColour) {
    personalizedCss = '\
    .help-desk-container .help-desk-style {\
      background: ' + context.webChatSettings.MainColour + '\
    }';
  }

  if (context.webChatSettings && context.webChatSettings.WebchatPosition) {
    if (context.webChatSettings.WebchatPosition == WebchatPosition.Left) {
      personalizedCss = personalizedCss + '\
      .help-desk-container .help-desk-style, iframe.help-desk-launcher-frame {\
        left: 20px; \
      }';
    }
    
    if (context.webChatSettings.WebchatPosition == WebchatPosition.Right) {
      personalizedCss = personalizedCss + '\
      .help-desk-container .help-desk-style, iframe.help-desk-launcher-frame {\
        right: 20px; \
      }';
    }

    if (context.webChatSettings.WebchatPosition == WebchatPosition.Middle) {
      personalizedCss = personalizedCss + '\
      .help-desk-container .help-desk-style, iframe.help-desk-launcher-frame {\
        right: 50%; \
        transform: translateX(50%); \
      }';
    }

    if (context.webChatSettings.WebchatPosition == WebchatPosition.Draggable) {
      personalizedCss = personalizedCss + '\
      .help-desk-container .help-desk-style, iframe.help-desk-launcher-frame {\
        right: 0px; \
      }';
    }
  } else {
    personalizedCss = personalizedCss + '\
      .help-desk-container .help-desk-style, iframe.help-desk-launcher-frame {\
        right: 20px; \
      }';
  }

  if (context.webChatSettings.CssUrl != null && context.webChatSettings.CssUrl != "") {
    customCssUrl = context.webChatSettings.CssUrl;
  }

  return (
    <DataContext.Provider value={context}>
      <div id="help-desk-container" className={classNames('help-desk-container', { 'webchat-draggable': context.webChatSettings.WebchatPosition == WebchatPosition.Draggable })}>
        <link rel="stylesheet" href="/css/webchat.css" />
        <style>
            {css}
            {personalizedCss}
        </style>
        
        <div className={classNames('help-desk-app', { 'maximize-chat': context.chatOpened }, { 'minimize-chat': !context.chatOpened })} aria-live="polite">
            {context.chatOpened && <ChatContainer />}
            <Frame className={classNames('help-desk-launcher-frame', 'help-desk-style', { 'show': !context.chatOpened }, { 'hide': context.chatOpened })}>
                <style>
                  {cssFrame}
                </style>
                <link rel="stylesheet" href="/css/webchat.css" />
                {
                  customCssUrl != '' && customCssUrl != null ? <link rel="stylesheet" href={customCssUrl} /> : <style></style>
                }
                <div id="help-desk-container" className={classNames('help-desk-container', { 'webchat-draggable-container': context.webChatSettings.WebchatPosition == WebchatPosition.Draggable })} role="main" aria-live="polite">
                    <div role="region">
                        <div className={classNames('help-desk-launcher', 'help-desk-style-iframe', { 'help-desk-launcher-active': context.chatOpened })} aria-label="Open Help Desk Messenger" role="button" onClick={() => !getActiveChatDisable && showHideChat()}>
                            <div className="help-desk-svg1">
                                { context.webChatSettings && context.webChatSettings.IconUrl ? 
                                  <img src={ context.webChatSettings.IconUrl } /> 
                                  :
                                  <svg id="svg" width="400" height="400" viewBox="0, 0, 400,400">
                                    <g id="svgg">
                                      <path id="path0" d="M76.518 98.489 L 76.524 141.303 199.638 242.569 C 267.351 298.265,322.918 343.951,323.121 344.092 C 323.485 344.348,323.488 343.364,323.488 205.274 L 323.488 66.198 288.608 66.198 L 253.728 66.198 253.681 133.170 L 253.633 200.143 165.263 128.026 C 116.660 88.362,76.807 55.857,76.702 55.792 C 76.574 55.712,76.514 69.547,76.518 98.489 " stroke="none" fill="#fbfbfb" fillRule="evenodd"/>
                                      <path id="path1" d="M76.512 256.076 L 76.512 333.646 76.965 333.302 C 77.214 333.113,79.057 331.608,81.060 329.958 C 83.063 328.308,92.542 320.507,102.126 312.623 C 111.709 304.739,120.731 297.310,122.175 296.113 C 123.619 294.917,126.953 292.174,129.583 290.018 C 132.213 287.862,135.293 285.327,136.428 284.386 C 138.260 282.865,156.505 267.850,166.938 259.276 C 169.848 256.885,170.771 256.028,170.626 255.853 C 170.465 255.658,156.298 243.945,154.453 242.480 C 153.849 242.000,151.088 239.730,147.492 236.754 C 146.357 235.816,142.560 232.695,139.053 229.819 C 135.546 226.943,131.749 223.820,130.614 222.879 C 129.480 221.938,119.986 214.133,109.517 205.533 C 99.048 196.933,89.555 189.128,88.420 188.189 C 83.938 184.476,76.968 178.779,76.746 178.646 C 76.559 178.534,76.512 194.070,76.512 256.076 " stroke="none" fill="#f37c2c" fillRule="evenodd"/>
                                      <path id="path2" d="M0.000 200.000 L 0.000 400.000 200.000 400.000 L 400.000 400.000 400.000 200.000 L 400.000 0.000 200.000 0.000 L 0.000 0.000 0.000 200.000 M165.120 127.772 L 253.633 200.020 253.681 133.109 L 253.728 66.198 288.702 66.198 L 323.676 66.198 323.676 205.360 C 323.676 294.873,323.611 344.500,323.494 344.460 C 323.311 344.397,76.992 141.819,76.559 141.375 C 76.295 141.104,76.201 55.502,76.465 55.517 C 76.542 55.521,116.437 88.036,165.120 127.772 M124.519 217.798 C 149.944 238.702,170.770 255.877,170.801 255.965 C 170.851 256.108,76.827 333.615,76.604 333.615 C 76.553 333.615,76.512 298.670,76.512 255.960 L 76.512 178.305 77.403 179.048 C 77.893 179.457,99.095 196.895,124.519 217.798 " stroke="none" fill="#242c34" fillRule="evenodd"/>
                                      <path id="path3" d="M76.417 98.359 C 76.417 121.978,76.438 131.613,76.465 119.769 C 76.492 107.926,76.492 88.601,76.465 76.825 C 76.438 65.049,76.417 74.740,76.417 98.359 " stroke="none" fill="#67625e" fillRule="evenodd"/>
                                      <path id="path4" d="M323.535 205.215 C 323.497 316.438,323.441 344.299,323.254 344.194 C 323.068 344.089,323.067 344.113,323.251 344.311 C 323.378 344.448,323.525 344.516,323.579 344.463 C 323.632 344.410,323.655 281.757,323.629 205.235 L 323.582 66.104 323.535 205.215 " stroke="none" fill="#84848c" fillRule="evenodd"/>
                                    </g>
                                  </svg> 
                                }
                            </div>
                            <div className="help-desk-svg2">
                                <svg focusable="false">
                                    <path d="M13.978 12.637l-1.341 1.341L6.989 8.33l-5.648 5.648L0 12.637l5.648-5.648L0 1.341 1.341 0l5.648 5.648L12.637 0l1.341 1.341L8.33 6.989l5.648 5.648z" fillRule="evenodd">
                                    </path>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </Frame>
        </div>
      </div>
    </DataContext.Provider>
  )
}